import { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { identityLogout } from '@/store/identitySlice';
import { PORTAL_BRANDING, PORTAL_NAV } from '@/config/portalConfig';
import { PortalSwitcher } from '@/modules/identity/components/PortalSwitcher';
import type { PortalCode } from '@/types/identity';

export function PortalShell() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const session = useAppSelector((s) => s.identity.session);
  const [navOpen, setNavOpen] = useState(false);

  if (!session) {
    return null;
  }

  const portalCode = session.activePortalCode as PortalCode;
  const branding = PORTAL_BRANDING[portalCode];
  const navItems = PORTAL_NAV[portalCode].filter(
    (item) => !item.permission || session.permissions.includes(item.permission),
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col md:flex-row">
      {navOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setNavOpen(false)}
        />
      )}

      <aside
        className={`fixed md:static inset-y-0 left-0 z-50 w-72 md:w-64 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col transform transition-transform duration-200 ${
          navOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        <div className={`px-5 py-6 bg-gradient-to-br ${branding.accentClass} text-white`}>
          <p className="text-xs uppercase tracking-wider opacity-80">Fattah One</p>
          <h1 className="text-lg font-semibold mt-1">{branding.title}</h1>
          <p className="text-sm opacity-90 mt-0.5">{branding.subtitle}</p>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.id}
              to={item.path}
              end={item.id === 'dashboard'}
              onClick={() => setNavOpen(false)}
              className={({ isActive }) =>
                `block rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-950 dark:text-primary-300'
                    : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-200 dark:border-slate-800 space-y-3">
          {session.availablePortals.length > 1 && <PortalSwitcher />}
          <button
            type="button"
            onClick={() => {
              dispatch(identityLogout());
              navigate('/identity/login', { replace: true });
            }}
            className="w-full text-left text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 md:ml-0">
        <header className="sticky top-0 z-30 border-b border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/90 backdrop-blur px-4 md:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              className="md:hidden rounded-lg border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-sm"
              onClick={() => setNavOpen((v) => !v)}
            >
              Menu
            </button>
            <div className="min-w-0">
              <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                {session.organizationName ?? 'Organization'}
              </p>
              <p className="font-medium text-slate-900 dark:text-slate-100 truncate">{session.email}</p>
            </div>
          </div>
          {session.availablePortals.length > 1 && (
            <Link
              to="/identity/portals"
              className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400 shrink-0"
            >
              All portals
            </Link>
          )}
        </header>

        <main className="flex-1 p-4 md:p-6 overflow-auto max-w-6xl w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
